import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Box,
  Badge,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import tramitesService, { HistorialCambio } from '../../../../services/tramitesObrasService';

interface ModalHistorialProps {
  isOpen: boolean;
  onClose: () => void;
  tramiteId: number;
}

const ACCIONES_COLORS: Record<string, string> = {
  CREADO: 'green',
  ACTUALIZADO: 'blue',
  CAMBIO_ESTATUS: 'orange',
  DOCUMENTO_AGREGADO: 'purple',
  DOCUMENTO_ELIMINADO: 'red',
  COMENTARIO: 'gray',
};

const ModalHistorial: React.FC<ModalHistorialProps> = ({ isOpen, onClose, tramiteId }) => {
  const [historial, setHistorial] = useState<HistorialCambio[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      cargarHistorial();
    }
  }, [isOpen, tramiteId]);

  const cargarHistorial = async () => {
    setLoading(true);
    try {
      const data = await tramitesService.obtenerHistorial(tramiteId);
      setHistorial(data);
    } catch (error) {
      console.error('Error al cargar historial:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-MX');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Historial de Cambios</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {loading ? (
            <Flex justify="center" p={8}>
              <Spinner size="lg" />
            </Flex>
          ) : historial.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={8}>
              No hay historial de cambios
            </Text>
          ) : (
            <VStack spacing={3} align="stretch">
              {historial.map((item) => (
                <Box
                  key={item.id}
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  borderLeftWidth="4px"
                  borderLeftColor={ACCIONES_COLORS[item.tipoAccion] + '.500' || 'gray.500'}
                >
                  <Flex justify="space-between" align="start" mb={2}>
                    <Badge colorScheme={ACCIONES_COLORS[item.tipoAccion] || 'gray'}>
                      {item.tipoAccion}
                    </Badge>
                    <Text fontSize="xs" color="gray.600">
                      {formatFecha(item.fechaCambio)}
                    </Text>
                  </Flex>

                  {item.estatusAnterior && item.estatusNuevo && (
                    <Text fontSize="sm" mb={1}>
                      <strong>Estatus:</strong> {item.estatusAnterior} → {item.estatusNuevo}
                    </Text>
                  )}

                  {item.comentario && (
                    <Text fontSize="sm" mb={1}>
                      <strong>Comentario:</strong> {item.comentario}
                    </Text>
                  )}

                  {item.observaciones && (
                    <Text fontSize="sm" mb={1}>
                      <strong>Observaciones:</strong> {item.observaciones}
                    </Text>
                  )}

                  {item.documentoNombre && (
                    <Text fontSize="sm" mb={1}>
                      <strong>Documento:</strong> {item.documentoNombre}
                    </Text>
                  )}

                  {item.usuario && (
                    <Text fontSize="xs" color="gray.600" mt={2}>
                      Por: {item.usuario}
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalHistorial;
